import { useTranslation } from 'react-i18next';
import { useEffect, useMemo, useState } from 'react';
import { useListChecklistItems, useCreateChecklistItem, useDeleteChecklistItem } from '../../../services/automation.queries';
import type { IChecklistItem } from '../../../types';

export interface IEditSelectedChecklistItem {
  id: number;
  labelEn: string;
  labelAr: string;
}

export interface IEditStep2FormValues {
  selectedItems: number[];
  selectedItemsData: IEditSelectedChecklistItem[];
}

interface IEditStepTwoProps {
  serviceId: string;
  isActive: boolean;
  initialSelectedLabels?: string[];
  onSubmit: (data: IEditStep2FormValues) => void;
  submitRef: React.MutableRefObject<(() => void) | null>;
}

const EditStepTwo = ({ serviceId, isActive, initialSelectedLabels, onSubmit, submitRef }: IEditStepTwoProps) => {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const isRtl = language === 'ar';

  const { data: checklistData, isLoading } = useListChecklistItems(serviceId);
  const items = useMemo(() => {
    const raw: IChecklistItem[] = checklistData?.result ?? checklistData?.data ?? [];
    return [...raw].sort((a, b) => a.displayOrder - b.displayOrder);
  }, [checklistData]);

  const createChecklistItem = useCreateChecklistItem();
  const deleteChecklistItem = useDeleteChecklistItem();

  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [initialized, setInitialized] = useState(false);
  const [error, setError] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [newLabel, setNewLabel] = useState('');

  // Match saved checklist labels against master list IDs once items load
  useEffect(() => {
    if (!initialized && items.length > 0 && initialSelectedLabels && initialSelectedLabels.length > 0) {
      const labelsSet = new Set(initialSelectedLabels.map((l) => l.toLowerCase().trim()));
      const matchedIds = items
        .filter((item) => labelsSet.has(item.labelAr?.toLowerCase().trim() ?? '') || labelsSet.has(item.labelEn?.toLowerCase().trim() ?? ''))
        .map((item) => item.id);
      setSelectedIds(matchedIds);
      setInitialized(true);
    }
  }, [items, initialized, initialSelectedLabels]);

  // Expose submit to parent via ref — only when this step is active
  useEffect(() => {
    if (!isActive) return;
    submitRef.current = () => {
      if (selectedIds.length === 0) {
        setError(t('Automation.checklistRequired'));
        return;
      }
      setError('');
      const selectedItemsData = items
        .filter((item) => selectedIds.includes(item.id))
        .map((item) => ({ id: item.id, labelEn: item.labelEn, labelAr: item.labelAr }));
      onSubmit({ selectedItems: selectedIds, selectedItemsData });
    };
  }, [isActive, selectedIds, items, onSubmit, submitRef, t]);

  const toggleItem = (id: number) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
    if (error) setError('');
  };

  const handleDeleteItem = (e: React.MouseEvent, id: number) => {
    e.stopPropagation();
    if (deleteChecklistItem.isPending) return;
    deleteChecklistItem.mutate({ id, serviceId });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8" style={{ border: '2px solid #E8E8EA', borderTopColor: '#5B3FFF' }} />
      </div>
    );
  }

  const handleAddItem = () => {
    if (!newLabel.trim() || createChecklistItem.isPending) return;
    createChecklistItem.mutate(
      { serviceId, body: { label: newLabel.trim() } },
      {
        onSuccess: () => {
          setNewLabel('');
          setShowAddForm(false);
        },
      },
    );
  };

  return (
    <div className="flex flex-col" style={{ gap: 16 }}>
      <div
        className="flex flex-col"
        style={{ backgroundColor: '#FAFAFA', borderRadius: 12, padding: 16, gap: 16 }}
      >
        <div className="flex flex-col">
          {items.map((item, index) => {
            const isChecked = selectedIds.includes(item.id);
            const isLast = index === items.length - 1;

            return (
              <button
                key={item.id}
                type="button"
                onClick={() => toggleItem(item.id)}
                className="group flex items-center w-full cursor-pointer outline-none"
                style={{
                  gap: 12,
                  padding: '8px 0',
                  background: isChecked ? 'rgba(239, 236, 255, 0.3)' : 'transparent',
                  borderTop: 'none',
                  borderLeft: 'none',
                  borderRight: 'none',
                  borderBottom: isLast && !isChecked ? 'none' : `1px solid ${isChecked ? '#EFECFF' : '#E9E9E7'}`,
                }}
              >

                {/* Checkbox */}
                <div
                  className="shrink-0 flex items-center justify-center"
                  style={{ width: 30, paddingBottom: 4 }}
                >
                  <div style={{ padding: '8px 8px 4px 8px' }}>
                    {isChecked ? (
                      <div
                        className="flex items-center justify-center overflow-hidden"
                        style={{
                          width: 14,
                          height: 14,
                          borderRadius: 6,
                          backgroundColor: '#5B3FFF',
                          border: '0.538px solid #5B3FFF',
                        }}
                      >
                        <svg
                          width="8"
                          height="5"
                          viewBox="0 0 8 6"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M1 3L3 5L7 1"
                            stroke="white"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </div>
                    ) : (
                      <div
                        style={{
                          width: 14,
                          height: 14,
                          borderRadius: 6,
                          backgroundColor: '#FFFFFF',
                          border: '0.538px solid #CACACA',
                        }}
                      />
                    )}
                  </div>
                </div>

                {/* Label */}
                <div className="flex-1 min-w-0">
                  <p
                    className="truncate text-start"
                    style={{
                      fontFamily: 'Tajawal, sans-serif',
                      fontWeight: 700,
                      fontSize: 12,
                      color: '#331860',
                      lineHeight: '12px',
                      margin: 0,
                    }}
                  >
                    {isRtl ? item.labelAr : item.labelEn || item.labelAr}
                  </p>
                </div>
                                {/* Delete icon - shown on hover */}
                <div
                  className="shrink-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                  style={{
                    width: 24,
                    height: 24,
                    borderRadius: 6,
                    backgroundColor: 'rgba(255, 50, 50, 0.2)',
                  }}
                  onClick={(e) => handleDeleteItem(e, item.id)}
                >
                  {deleteChecklistItem.isPending && deleteChecklistItem.variables?.id === item.id ? (
                    <div className="animate-spin rounded-full" style={{ width: 14, height: 14, border: '2px solid rgba(255,50,50,0.3)', borderTopColor: 'rgba(255,50,50,1)' }} />
                  ) : (
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M10.5 3.5L3.5 10.5M3.5 3.5L10.5 10.5" stroke="rgba(255, 50, 50, 0.8)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  )}
                </div>

              </button>
            );
          })}
        </div>

      </div>

      {/* Add checklist item: inline input bar or "+ مهمة فرعية" button */}
      {showAddForm ? (
        <div
          className="flex items-center"
          style={{
            backgroundColor: '#FAFAFF',
            borderTop: '1px solid #5B3FFF',
            boxShadow: '0px -4px 12px 0px rgba(0,0,0,0.03)',
            height: 56,
            padding: '0 24px',
            gap: 12,
          }}
        >
          {/* Text input */}
          <input
            type="text"
            dir="auto"
            placeholder={t('Automation.addChecklistPlaceholder')}
            value={newLabel}
            onChange={(e) => setNewLabel(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleAddItem(); if (e.key === 'Escape') { setShowAddForm(false); setNewLabel(''); } }}
            autoFocus
            className="outline-none"
            style={{
              flex: 1,
              fontFamily: 'Tajawal, sans-serif',
              fontWeight: 700,
              fontSize: 12,
              color: '#1A1A2E',
              backgroundColor: 'transparent',
              border: 'none',
              textAlign: isRtl ? 'right' : 'left',
            }}
          />
          {/* Cancel & Confirm circular buttons */}
          <div className="flex items-center shrink-0" style={{ gap: 16 }}>
            <button
              type="button"
              onClick={handleAddItem}
              disabled={createChecklistItem.isPending || !newLabel.trim()}
              className="flex items-center justify-center cursor-pointer outline-none"
              style={{
                width: 32,
                height: 32,
                borderRadius: 16,
                backgroundColor: !newLabel.trim() ? '#ECECEC' : '#4415fc',
                border: 'none',
                opacity: createChecklistItem.isPending ? 0.6 : 1,
              }}
            >
              {createChecklistItem.isPending ? (
                <div className="animate-spin rounded-full" style={{ width: 14, height: 14, border: '2px solid rgba(255,255,255,0.3)', borderTopColor: '#fff' }} />
              ) : (
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M2.5 7L5.5 10L11.5 4" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              )}
            </button>
            <button
              type="button"
              onClick={() => { setShowAddForm(false); setNewLabel(''); }}
              className="flex items-center justify-center cursor-pointer outline-none"
              style={{
                width: 32,
                height: 32,
                borderRadius: 16,
                backgroundColor: '#ECECEC',
                border: 'none',
              }}
            >
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M10.5 3.5L3.5 10.5M3.5 3.5L10.5 10.5" stroke="#666" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>

          </div>
        </div>
      ) : (
        <div
          className="flex items-center justify-start cursor-pointer"
          style={{
            backgroundColor: '#FFFFFF',
            borderTop: '1px solid #F3F4F6',
            padding: '16px 24px',
          }}
          onClick={() => setShowAddForm(true)}
        >
          <div className="flex items-center" style={{ gap: 6 }}>
            <span
              style={{
                fontFamily: 'Tajawal, sans-serif',
                fontWeight: 700,
                fontSize: 12,
                color: '#5B3FFF',
              }}
            >
              {t('Automation.addChecklistItem')}
            </span>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M8 3.5V12.5M3.5 8H12.5" stroke="#5B3FFF" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
        </div>
      )}

      {error && (
        <p
          style={{
            fontFamily: 'Tajawal, sans-serif',
            fontSize: 12,
            color: '#EF4444',
            margin: 0,
          }}
        >
          {error}
        </p>
      )}
    </div>
  );
};

export default EditStepTwo;
